/**
 * 🔒 LOCKED — March 27, 2026
 * MicroAssessmentPage — Focus topic entry + TTS toggle.
 * APPROVED BY OWNER. DO NOT MODIFY without explicit approval.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { validateFocusTopic } from '../utils/inputValidation';

const TIER_ACCENT = {
	foundations: '#38bdf8',
	operator: '#34d399',
	certified: '#a78bfa',
};

const BASE_LOGS = [
	'[NEURAL_NEXUS]: Assessment channel initialized.',
	'[USER_CONTEXT]: Blueprint signal received.',
	'[DATA_PIPELINE]: Preparing baseline calibration.',
	'[INSTRUCTOR_LAYER]: Waiting on learner start event.',
];

const SPINNER_MESSAGES = [
	'Preparing assessment environment',
	'Loading placement criteria',
	'Aligning examiner parameters',
	'Configuring response model',
	'Staging assessment flow',
];

const AZURE_PREVIEW_VOICES = [
	'en-US-JennyNeural',
	'en-US-AriaNeural',
	'en-US-AvaMultilingualNeural',
	'en-US-AndrewNeural',
	'en-GB-SoniaNeural',
];

function deriveFocusTopic(input) {
	const cleaned = String(input || '').replace(/\s+/g, ' ').trim();
	if (!cleaned) return 'current priority workflow';
	if (cleaned.length <= 54) return cleaned;
	return `${cleaned.slice(0, 51).trim()}...`;
}

function makeLogLine() {
	const now = new Date();
	const time = now.toTimeString().slice(0, 8);
	const message = SPINNER_MESSAGES[Math.floor(Math.random() * SPINNER_MESSAGES.length)];
	return `[${time}] [NEURAL_NEXUS]: ${message}.`;
}

function wait(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function MicroAssessmentPage() {
	const location = useLocation();
	const navigate = useNavigate();
	const audioRef = useRef(null);
	const fns = getFunctions();
	const openaiTTSFn = httpsCallable(fns, 'openaiTTS');

	const [curtainVisible, setCurtainVisible] = useState(true);
	const [curtainOpacity, setCurtainOpacity] = useState(1);
	const [terminalHot, setTerminalHot] = useState(false);
	const [logs, setLogs] = useState(() => [...BASE_LOGS]);
	const [learnerFocus, setLearnerFocus] = useState('');
	const [topicLocked, setTopicLocked] = useState(false);
	const [focusError, setFocusError] = useState('');
	const [ttsState, setTtsState] = useState('idle');
	const [ttsMuted, setTtsMuted] = useState(true);
	const [ttsExpanded, setTtsExpanded] = useState(false);
	const [ttsProvider, setTtsProvider] = useState('openai');
	const [openaiVoice, setOpenaiVoice] = useState('nova');
	const [azurePreviewVoice, setAzurePreviewVoice] = useState(AZURE_PREVIEW_VOICES[0]);

	const activeTier = location.state?.activeTier || 'foundations';
	const accent = TIER_ACCENT[activeTier] || TIER_ACCENT.foundations;

	// Cinematic curtain — fade out after mount to cover TTS/Firebase init
	useEffect(() => {
		const fadeStart = setTimeout(() => setCurtainOpacity(0), 300);
		const fadeEnd = setTimeout(() => setCurtainVisible(false), 1800);
		return () => { clearTimeout(fadeStart); clearTimeout(fadeEnd); };
	}, []);

	useEffect(() => {
		const interval = setInterval(() => {
			setLogs((prev) => [...prev.slice(-23), makeLogLine()]);
		}, 1800);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current = null;
			}
		};
	}, []);

	function appendLog(line) {
		setLogs((prev) => [...prev.slice(-23), line]);
	}

	function runTtsCalibration() {
		if (ttsMuted) {
			setTtsState('muted');
			appendLog('[TTS]: Voice preview blocked because mute is enabled.');
			return;
		}

		if (ttsProvider === 'azure-preview') {
			setTtsState('ready');
			appendLog(`[TTS]: Azure Neural preview selected -> ${azurePreviewVoice}. (Catalog preview mode only.)`);
			return;
		}

		(async () => {
			try {
				setTtsState('playing');
				setTerminalHot(true);
				appendLog(`[TTS]: Calling OpenAI TTS function (${openaiVoice})...`);

				const tts = await openaiTTSFn({
					text: 'Voice calibration preview. Spoken interaction is available for this course experience.',
					voice: openaiVoice,
					speed: 1.0,
				});

				appendLog('[TTS]: Cloud function returned. Decoding audio...');

				const b64 = tts?.data?.audio;
				if (!b64) throw new Error('No audio in response — data keys: ' + Object.keys(tts?.data || {}).join(', '));

				const binary = atob(b64);
				const bytes = new Uint8Array(binary.length);
				for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
				const blob = new Blob([bytes], { type: tts?.data?.mimeType || 'audio/mpeg' });
				const url = URL.createObjectURL(blob);
				const player = new Audio(url);
				audioRef.current = player;

				player.onerror = (e) => {
					appendLog(`[TTS]: Audio playback error — ${player.error?.message || 'unknown'}`);
					setTtsState('unsupported');
					setTerminalHot(false);
				};

				player.onended = () => {
					URL.revokeObjectURL(url);
					if (audioRef.current === player) audioRef.current = null;
					setTtsState('ready');
					setTerminalHot(false);
					appendLog('[TTS]: Voice calibration preview complete.');
				};

				appendLog(`[TTS]: Playing audio (${(blob.size / 1024).toFixed(1)} KB)...`);
				await player.play();
			} catch (err) {
				setTtsState('unsupported');
				setTerminalHot(false);
				const msg = err?.message || err?.code || String(err);
				appendLog(`[TTS]: FAILED — ${msg}`);
				console.error('[TTS calibration error]', err);
			}
		})();
	}

	function toggleTtsMute() {
		setTtsMuted((prev) => {
			const next = !prev;
			if (next && audioRef.current) {
				audioRef.current.pause();
				audioRef.current = null;
				setTtsState('muted');
				appendLog('[TTS]: Muted by learner.');
			}
			if (!next) {
				setTtsState('idle');
				appendLog('[TTS]: Unmuted and available.');
			}
			return next;
		});
	}

	const focusValidation = validateFocusTopic(learnerFocus);
	const focusIsValid = focusValidation.valid;

	function proceedToAssessment() {
		const payload = {
			source: 'micro-assessment',
			activeTier,
			focusTopic: deriveFocusTopic(learnerFocus),
			tts: {
				provider: ttsProvider,
				openaiVoice,
				azurePreviewVoice,
				muted: ttsMuted,
				state: ttsState,
			},
			timestamp: Date.now(),
		};

		// Pre-fetch placement intro audio NOW — we're inside a button click (user gesture).
		// Storing the Promise means PlacementAssessmentPage can play in .then()
		// without needing its own user gesture, masking the TTS latency behind the curtain.
		if (!payload.tts.muted && payload.tts.provider === 'openai') {
			const introText = 'Placement Assessment. You have 6 questions. Three are about your focus area. Three test how you think about directing AI in general. Answer each one completely and honestly — describe what you actually do, not what you think the right answer is. There are no right answers. Read all 6 questions before you start.';
			window.__omPlacementTTS = openaiTTSFn({ text: introText, voice: payload.tts.openaiVoice || 'nova' });
		} else {
			window.__omPlacementTTS = null;
		}

		try {
			sessionStorage.setItem('om_assessment_context', JSON.stringify({ micro: payload }));
		} catch {
			// Non-fatal: navigation state still carries context.
		}

		navigate('/placement-assessment', {
			state: {
				assessmentContext: { micro: payload },
				chatTheme: 'assessment-cinematic',
			},
		});
	}

	return (
		<main
			style={{
				minHeight: '100vh',
				color: '#e9f3ff',
				padding: '28px 20px',
				position: 'relative',
				background:
					'repeating-linear-gradient(-24deg, rgba(152,236,255,0.05) 0, rgba(152,236,255,0.05) 1px, transparent 1px, transparent 84px), radial-gradient(120% 80% at 18% 16%, rgba(56,189,248,0.2), transparent 52%), radial-gradient(120% 90% at 76% 74%, rgba(167,139,250,0.16), transparent 58%), linear-gradient(165deg, #040716 0%, #091130 44%, #080f26 100%)',
			}}
		>
			{curtainVisible && (
				<div
					style={{
						position: 'fixed',
						inset: 0,
						zIndex: 9999,
						background: 'linear-gradient(165deg, #040716 0%, #091130 44%, #080f26 100%)',
						opacity: curtainOpacity,
						transition: 'opacity 1.4s ease-in-out',
						pointerEvents: 'none',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<div style={{ textAlign: 'center' }}>
						<div style={{ fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(155, 233, 255, 0.5)' }}>
							Initializing Assessment Environment
						</div>
					</div>
				</div>
			)}


			<div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gap: '18px', gridTemplateColumns: 'minmax(0, 1fr) 320px', alignItems: 'start', filter: 'drop-shadow(0 22px 40px rgba(1,6,20,0.38))' }}>
				<div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
					<section
						style={{
							border: '1px solid rgba(153,179,214,0.22)',
							borderRadius: '14px',
							padding: '18px',
							background: 'linear-gradient(165deg, rgba(8,18,44,0.9), rgba(7,12,29,0.7))',
							boxShadow: '0 34px 78px rgba(1,6,20,0.58), 0 10px 22px rgba(1,6,20,0.38), inset 0 1px 0 rgba(233,243,255,0.09)',
						}}
					>
						<h1 style={{ margin: '0 0 8px 0', fontSize: '1.35rem' }}>Placement Assessment Prep</h1>
						<p style={{ margin: 0, color: '#99b3d6', lineHeight: 1.6 }}>
							Before the assessment begins, we need one thing from you — a real task or topic you work on.
							The AI examiner will use it to build questions around your actual workflow.
						</p>

						<div style={{ marginTop: '14px', display: 'grid', gap: '8px' }}>
							<label htmlFor="learner-focus" style={{ fontSize: '0.84rem', color: '#c8eeff', letterSpacing: '0.02em' }}>
									Your Focus Area
								</label>
								<div style={{ fontSize: '0.83rem', color: '#d9ecff', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.01em', textShadow: '0 0 12px rgba(56,189,248,0.24)' }}>
									What do you actually use AI for — or want to? The examiner will weave your answer into the assessment.
							</div>
							{topicLocked ? (
								<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
									<div style={{
										flex: 1,
										background: 'rgba(52,211,153,0.08)',
										border: '1px solid rgba(52,211,153,0.35)',
										borderRadius: '10px',
										padding: '10px 12px',
										color: '#a7f3d0',
										fontSize: '0.9rem',
										lineHeight: 1.45,
									}}>
										{learnerFocus}
									</div>
									<button
										type="button"
										onClick={() => setTopicLocked(false)}
										style={{
											border: '1px solid rgba(153,179,214,0.3)',
											background: 'transparent',
											color: '#99b3d6',
											borderRadius: '8px',
											padding: '6px 10px',
											fontSize: '0.76rem',
											cursor: 'pointer',
											whiteSpace: 'nowrap',
										}}
									>
										Edit
									</button>
								</div>
							) : (
								<>
									<textarea
										id="learner-focus"
										value={learnerFocus}
										onChange={(event) => {
											setLearnerFocus(event.target.value);
											setFocusError('');
										}}
										onKeyDown={(event) => {
											if (event.key === 'Enter' && !event.shiftKey) {
												event.preventDefault();
												const v = validateFocusTopic(learnerFocus);
												if (v.valid) {
													setFocusError('');
													setTopicLocked(true);
													appendLog(`[LEARNER]: Focus area locked — "${deriveFocusTopic(learnerFocus)}"`);
												} else if (v.reason) {
													setFocusError(v.reason);
												}
											}
										}}
										placeholder="Example: writing client proposals, building ad campaigns, documenting SOPs"
										rows={3}
										style={{
											width: '100%',
											background: 'rgba(9,17,40,0.9)',
											border: '1px solid rgba(153,179,214,0.35)',
											borderRadius: '10px',
											padding: '10px 12px',
											color: '#e9f3ff',
											fontSize: '0.9rem',
											resize: 'vertical',
										}}
									/>
									{focusError && (
										<div style={{ fontSize: '0.78rem', color: '#f87171', marginTop: '6px', lineHeight: 1.4 }}>
											{focusError}
										</div>
									)}
								</>
							)}
						</div>

						{/* Compact voice toggle */}
						<div style={{ marginTop: '12px' }}>
							<button
								type="button"
								onClick={() => {
									if (ttsMuted) {
										toggleTtsMute();
										setTtsExpanded(true);
									} else {
										toggleTtsMute();
										setTtsExpanded(false);
									}
								}}
								style={{
									border: `1px solid ${!ttsMuted ? 'rgba(208,171,98,0.5)' : 'rgba(153,179,214,0.3)'}`,
									background: !ttsMuted ? 'linear-gradient(145deg, rgba(208,171,98,0.18), rgba(208,171,98,0.06))' : 'transparent',
									color: !ttsMuted ? '#f6dfa5' : '#7f95b6',
									borderRadius: '8px',
									padding: '6px 12px',
									fontSize: '0.78rem',
									fontWeight: 600,
									cursor: 'pointer',
									transition: 'all 200ms ease',
								}}
							>
								{!ttsMuted ? '🔊 Voice Enabled' : '🔇 Want spoken responses? Enable voice'}
							</button>

							{ttsExpanded && !ttsMuted && (
								<div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px', alignItems: 'center' }}>
									<select
										value={ttsProvider}
										onChange={(event) => setTtsProvider(event.target.value)}
										style={{
											border: '1px solid rgba(208,171,98,0.45)',
											background: 'rgba(12,15,26,0.82)',
											color: '#f7e3b7',
											borderRadius: '8px',
											padding: '6px 8px',
											fontSize: '0.74rem',
										}}
									>
										<option value="openai">OpenAI Voice</option>
										<option value="azure-preview">Azure Neural Preview</option>
									</select>
									{ttsProvider === 'openai' ? (
										<select
											value={openaiVoice}
											onChange={(event) => setOpenaiVoice(event.target.value)}
											style={{
												border: '1px solid rgba(208,171,98,0.45)',
												background: 'rgba(12,15,26,0.82)',
												color: '#f7e3b7',
												borderRadius: '8px',
												padding: '6px 8px',
												fontSize: '0.74rem',
											}}
										>
											<option value="onyx">Jarvis (Onyx)</option>
											<option value="nova">Nova</option>
											<option value="alloy">Alloy</option>
											<option value="echo">Echo</option>
										</select>
									) : (
										<select
											value={azurePreviewVoice}
											onChange={(event) => setAzurePreviewVoice(event.target.value)}
											style={{
												border: '1px solid rgba(208,171,98,0.45)',
												background: 'rgba(12,15,26,0.82)',
												color: '#f7e3b7',
												borderRadius: '8px',
												padding: '6px 8px',
												fontSize: '0.74rem',
											}}
										>
											{AZURE_PREVIEW_VOICES.map((voice) => (
												<option key={voice} value={voice}>{voice}</option>
											))}
										</select>
									)}
									<button
										type="button"
										onClick={runTtsCalibration}
										style={{
											border: '1px solid rgba(208,171,98,0.62)',
											background: 'linear-gradient(145deg, rgba(208,171,98,0.28), rgba(208,171,98,0.14))',
											color: '#f7e3b7',
											borderRadius: '8px',
											padding: '6px 9px',
											fontWeight: 600,
											cursor: 'pointer',
											fontSize: '0.74rem',
											whiteSpace: 'nowrap',
										}}
									>
										{ttsState === 'playing' ? 'Previewing...' : 'Preview Voice'}
									</button>
								</div>
							)}
						</div>

						<div style={{ marginTop: '14px', border: `1px solid ${accent}55`, borderRadius: '10px', padding: '12px', background: `linear-gradient(145deg, ${accent}1f, rgba(8, 16, 34, 0.72))`, width: 'min(520px, 100%)', boxShadow: `0 26px 54px rgba(1,6,20,0.5), 0 0 18px ${accent}18` }}>
							<div style={{ fontSize: '0.82rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#c8eeff', marginBottom: '4px' }}>
								Ready to Begin
							</div>
							<p style={{ margin: 0, color: '#9cb2d3', lineHeight: 1.55 }}>
								The AI examiner will ask you 6 questions. Three are built around your focus area. Three test whether you can bypass fundamentals — how you think about constraints, structure, and directing AI output.
							</p>
							<p style={{ margin: '10px 0 0 0', color: '#d0ab62', fontSize: '0.82rem', lineHeight: 1.5, fontWeight: 500 }}>
								We recommend completing the assessment in one sitting. If you need to take a break, take it now — once you start, the examiner is live.
							</p>
							<div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
								<button
									type="button"
									onClick={() => {
										const v = validateFocusTopic(learnerFocus);
										if (!v.valid) {
											setFocusError(v.reason || 'Please enter a valid focus area.');
											appendLog('[SYSTEM]: Focus area required before starting assessment.');
											return;
										}
										setFocusError('');
										if (!topicLocked) setTopicLocked(true);
										proceedToAssessment();
									}}
									disabled={!focusIsValid}
									style={{
										border: '1px solid rgba(56,189,248,0.82)',
										background: focusIsValid
											? 'linear-gradient(145deg, rgba(56,189,248,0.38), rgba(56,189,248,0.18))'
											: 'rgba(56,189,248,0.08)',
										color: focusIsValid ? '#e9f3ff' : '#5a7a9e',
										borderRadius: '10px',
										padding: '9px 13px',
										fontWeight: 600,
										cursor: focusIsValid ? 'pointer' : 'not-allowed',
										boxShadow: focusIsValid ? '0 0 14px rgba(56,189,248,0.35)' : 'none',
										transition: 'all 200ms ease',
									}}
								>
									Start Assessment
								</button>
							</div>
							<p style={{ margin: '8px 0 0 0', color: '#92a9ca', fontSize: '0.78rem' }}>
								Voice: {ttsMuted ? 'text only' : ttsState === 'playing' ? 'previewing' : ttsState === 'unsupported' ? 'not available in this browser' : ttsState === 'ready' ? 'ready' : 'enabled'}
							</p>
						</div>
					</section>


				</div>

				<aside>
					<section
						style={{
							border: '1px solid rgba(153,179,214,0.2)',
							borderRadius: '14px',
							padding: '14px',
							background: 'linear-gradient(165deg, rgba(8,18,44,0.9), rgba(7,12,29,0.78))',
							boxShadow: `0 34px 78px rgba(1,6,20,0.58), 0 12px 24px rgba(1,6,20,0.42), 0 0 26px ${accent}18, inset 0 1px 0 rgba(233,243,255,0.08)`,
							maxHeight: '560px',
							overflow: 'hidden',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
							<h2 style={{ margin: 0, fontSize: '0.95rem' }}>Live Data</h2>
							<span
								style={{
									fontSize: '0.72rem',
									fontWeight: 600,
									letterSpacing: '0.05em',
									textTransform: 'uppercase',
									padding: '4px 8px',
									borderRadius: '999px',
									color: terminalHot ? '#0b1220' : '#99b3d6',
									background: terminalHot ? accent : 'rgba(153,179,214,0.14)',
								}}
							>
								{terminalHot ? 'Sync burst' : 'Nominal'}
							</span>
						</div>

						<ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '8px', fontSize: '0.8rem', color: '#99b3d6', overflowY: 'auto', minHeight: 0, flex: 1 }}>
							{logs.slice(-20).map((line, index) => {
								const dividerIndex = line.indexOf(']: ');
								const label = dividerIndex !== -1 ? `${line.slice(0, dividerIndex + 1)}:` : line;
								const message = dividerIndex !== -1 ? line.slice(dividerIndex + 3).trim() : '';
								return (
									<li key={`${line}-${index}`} style={{ borderBottom: '1px solid rgba(153,179,214,0.12)', paddingBottom: '7px' }}>
										<strong style={{ color: '#c8eeff' }}>{label}</strong>
										{message ? ` ${message}` : ''}
									</li>
								);
							})}
						</ul>

						<p style={{ margin: '10px 0 0 0', fontSize: '0.78rem', color: '#7f95b6' }}>
							This stream reflects your activity on the page in real time.
						</p>
					</section>
				</aside>
			</div>

		</main>
	);
}
